/**
 * useAgentTerminalActivity — true while the agent is driving the given terminal session.
 *
 * Reuses the existing event stream (no new backend events): it watches
 * `flow_message.tool_call_args_delta` events for Tool_CanvasTerminal calls, pulls the
 * target session_id out of the streamed args, and reports the matching terminal as active
 * for a short window after the last delta (terminal tool calls can run for many seconds
 * while the agent waits, and it typically polls in bursts, so a fade window reads well).
 */
import { useEventStream } from "@/providers/EventStreamProvider";
import { useEffect, useRef, useState } from "react";

const CANVAS_TERMINAL_EXECUTION_NAME = "Tool_CanvasTerminal";
const ACTIVE_WINDOW_MS = 4000;
const SESSION_ID_RE = /"session_id"\s*:\s*"([^"]+)"/;

export function useAgentTerminalActivity(sessionId: string): boolean {
  const { registerHandler } = useEventStream();
  const [active, setActive] = useState(false);
  const argsByCallRef = useRef<Map<string, string>>(new Map());
  // Once a call's session_id resolves we cache the ours/not-ours decision and
  // drop its accumulated args, so a long canvas session can't grow the args
  // buffer without bound (the decision is tiny and final).
  const decidedByCallRef = useRef<Map<string, boolean>>(new Map());
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!sessionId) return;

    const bump = () => {
      setActive(true);
      if (fadeRef.current) clearTimeout(fadeRef.current);
      fadeRef.current = setTimeout(() => setActive(false), ACTIVE_WINDOW_MS);
    };

    const unregister = registerHandler((event) => {
      if (event.type !== "flow_message.tool_call_args_delta") return;
      const payload = event.data.flow_message_tool_call_args_delta;
      const call = payload.tool_call;
      if (
        call?.function?.execution_tool_name !== CANVAS_TERMINAL_EXECUTION_NAME
      )
        return;

      const callId = call.id ?? "";

      // Already resolved this call's target — keep pulsing if it's ours, but
      // don't re-accumulate args (bounded memory over long sessions).
      const decided = decidedByCallRef.current.get(callId);
      if (decided !== undefined) {
        if (decided) bump();
        return;
      }

      const acc =
        (argsByCallRef.current.get(callId) ?? "") + (payload.args_delta ?? "");
      const match = acc.match(SESSION_ID_RE);
      if (match) {
        // session_id has fully streamed in — record the decision and free the
        // accumulated args; further deltas for this call use the cache above.
        const isOurs = match[1] === sessionId;
        decidedByCallRef.current.set(callId, isOurs);
        argsByCallRef.current.delete(callId);
        if (isOurs) bump();
        return;
      }

      // session_id not yet streamed: keep accumulating and pulse optimistically
      // (single-terminal case where the agent omits it — it can only be this one).
      argsByCallRef.current.set(callId, acc);
      bump();
    });

    return () => {
      unregister();
      if (fadeRef.current) clearTimeout(fadeRef.current);
      argsByCallRef.current.clear();
      decidedByCallRef.current.clear();
    };
  }, [registerHandler, sessionId]);

  return active;
}
